import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { FiArrowLeft } from 'react-icons/fi';
import './LegalPages.css';

const PrivacyPolicy = () => {
  const { t, i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const en = (
    <>
      <p className="legal-updated">Last Updated: April 11, 2026</p>

      <h2>1. Introduction</h2>
      <p>Roolink ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our website (roolink.net), client portal (portal.roolink.net), mobile applications, and services including IT Consultation, Android & iOS App Development, Web Development, Linux System Administration, Domain Registration, Web Hosting, and AI Solutions.</p>

      <h2>2. Information We Collect</h2>
      <h3>2.1 Personal Information</h3>
      <ul>
        <li>Full name, email address, phone number, and mailing address</li>
        <li>Company name and job title</li>
        <li>Billing and payment information (credit/debit card details, KNET, bank transfer details)</li>
        <li>Government-issued ID (when required for domain registration)</li>
        <li>Login credentials (username and password)</li>
      </ul>

      <h3>2.2 Technical Information</h3>
      <ul>
        <li>IP address, browser type, operating system, and device information</li>
        <li>Website usage data, pages visited, and interaction patterns</li>
        <li>Cookies and similar tracking technologies</li>
        <li>Server logs and access records</li>
      </ul>

      <h3>2.3 Service-Specific Data</h3>
      <ul>
        <li><strong>Web Hosting:</strong> Website files, databases, email content stored on our servers</li>
        <li><strong>Domain Registration:</strong> WHOIS information as required by ICANN</li>
        <li><strong>App Development:</strong> Project specifications, design assets, and app content provided by clients</li>
        <li><strong>AI Solutions:</strong> Data provided for AI model training, prompts, and generated outputs</li>
        <li><strong>Support:</strong> Communication records, support tickets, and chat logs</li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <ul>
        <li>To provide, maintain, and improve our services</li>
        <li>To process transactions and send billing notifications</li>
        <li>To communicate with you about your account, services, and support requests</li>
        <li>To comply with legal obligations (e.g., ICANN domain registration requirements)</li>
        <li>To detect, prevent, and address technical issues and security threats</li>
        <li>To send marketing communications (with your consent)</li>
        <li>To personalize your experience and provide relevant recommendations</li>
      </ul>

      <h2>4. Data Sharing & Disclosure</h2>
      <p>We do not sell your personal information. We may share your data with:</p>
      <ul>
        <li><strong>Service Providers:</strong> Payment processors, domain registrars, hosting infrastructure providers (AWS, Google Cloud, OVH, Cloudflare)</li>
        <li><strong>Legal Requirements:</strong> When required by law, court order, or governmental authority</li>
        <li><strong>ICANN:</strong> Domain registration data as required by ICANN policies</li>
        <li><strong>AI Partners:</strong> Anonymized data may be processed by AI service providers (OpenAI, Anthropic, Google) for AI-powered features</li>
        <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets</li>
      </ul>

      <h2>5. Data Security</h2>
      <p>We implement industry-standard security measures including:</p>
      <ul>
        <li>SSL/TLS encryption for all data transmission</li>
        <li>Encrypted storage for sensitive data and passwords</li>
        <li>Regular security audits and vulnerability assessments</li>
        <li>Firewall protection and DDoS mitigation (Cloudflare)</li>
        <li>Access controls and multi-factor authentication for administrative systems</li>
        <li>Regular data backups with encrypted storage</li>
      </ul>

      <h2>6. Data Retention</h2>
      <p>We retain your personal data for as long as your account is active or as needed to provide services. After account termination:</p>
      <ul>
        <li>Account data is retained for 90 days before permanent deletion</li>
        <li>Billing records are retained for 7 years as required by Kuwait commercial law</li>
        <li>Server logs are retained for 12 months</li>
        <li>Backup data is purged within 30 days of account deletion</li>
      </ul>

      <h2>7. Cookies</h2>
      <p>We use cookies for:</p>
      <ul>
        <li><strong>Essential Cookies:</strong> Authentication, security, and session management</li>
        <li><strong>Analytics Cookies:</strong> Understanding usage patterns to improve our services</li>
        <li><strong>Preference Cookies:</strong> Remembering your language and display preferences</li>
      </ul>
      <p>You can control cookies through your browser settings.</p>

      <h2>8. Your Rights</h2>
      <p>You have the right to:</p>
      <ul>
        <li>Access your personal data</li>
        <li>Correct inaccurate information</li>
        <li>Request deletion of your data (see our <a href="/delete-account">Account Deletion</a> page)</li>
        <li>Export your data in a portable format</li>
        <li>Withdraw consent for marketing communications</li>
        <li>Object to data processing</li>
      </ul>

      <h2>9. Children's Privacy</h2>
      <p>Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children.</p>

      <h2>10. International Data Transfers</h2>
      <p>Your data may be processed in servers located outside Kuwait, including the United States, Europe, and other regions where our infrastructure providers operate. We ensure appropriate safeguards are in place for such transfers.</p>

      <h2>11. Changes to This Policy</h2>
      <p>We may update this Privacy Policy from time to time. We will notify you of significant changes via email or through our website.</p>

      <h2>12. Contact Us</h2>
      <p>For privacy-related inquiries:</p>
      <ul>
        <li>Email: <a href="mailto:info@roolink.net">info@roolink.net</a></li>
        <li>Phone: <a href="tel:+96550580600" dir="ltr">+965 50580600</a></li>
        <li>Address: Kuwait</li>
      </ul>
    </>
  );

  const ar = (
    <>
      <p className="legal-updated">آخر تحديث: 11 أبريل 2026</p>

      <h2>1. المقدمة</h2>
      <p>تلتزم رولينك ("نحن" أو "لنا") بحماية خصوصيتك. توضح سياسة الخصوصية هذه كيفية جمع واستخدام والكشف عن وحماية معلوماتك عند استخدامك لموقعنا الإلكتروني (roolink.net) وبوابة العملاء (portal.roolink.net) وتطبيقات الجوال وخدماتنا بما في ذلك الاستشارات التقنية، تطوير تطبيقات أندرويد وiOS، تطوير الويب، إدارة أنظمة لينكس، تسجيل النطاقات، استضافة المواقع، وحلول الذكاء الاصطناعي.</p>

      <h2>2. المعلومات التي نجمعها</h2>
      <h3>2.1 المعلومات الشخصية</h3>
      <ul>
        <li>الاسم الكامل والبريد الإلكتروني ورقم الهاتف والعنوان البريدي</li>
        <li>اسم الشركة والمسمى الوظيفي</li>
        <li>معلومات الفوترة والدفع (تفاصيل بطاقة الائتمان/الخصم، كي نت، التحويل البنكي)</li>
        <li>الهوية الحكومية (عند الحاجة لتسجيل النطاقات)</li>
        <li>بيانات تسجيل الدخول (اسم المستخدم وكلمة المرور)</li>
      </ul>

      <h3>2.2 المعلومات التقنية</h3>
      <ul>
        <li>عنوان IP ونوع المتصفح ونظام التشغيل ومعلومات الجهاز</li>
        <li>بيانات استخدام الموقع والصفحات المزارة وأنماط التفاعل</li>
        <li>ملفات تعريف الارتباط (الكوكيز) وتقنيات التتبع المماثلة</li>
        <li>سجلات الخادم وسجلات الوصول</li>
      </ul>

      <h3>2.3 بيانات خاصة بالخدمات</h3>
      <ul>
        <li><strong>استضافة المواقع:</strong> ملفات الموقع وقواعد البيانات ومحتوى البريد الإلكتروني المخزن على خوادمنا</li>
        <li><strong>تسجيل النطاقات:</strong> معلومات WHOIS كما هو مطلوب من ICANN</li>
        <li><strong>تطوير التطبيقات:</strong> مواصفات المشروع وأصول التصميم ومحتوى التطبيق المقدم من العملاء</li>
        <li><strong>حلول الذكاء الاصطناعي:</strong> البيانات المقدمة لتدريب نماذج الذكاء الاصطناعي والمدخلات والمخرجات المولدة</li>
        <li><strong>الدعم:</strong> سجلات الاتصال وتذاكر الدعم وسجلات المحادثة</li>
      </ul>

      <h2>3. كيف نستخدم معلوماتك</h2>
      <ul>
        <li>لتوفير وصيانة وتحسين خدماتنا</li>
        <li>لمعالجة المعاملات وإرسال إشعارات الفوترة</li>
        <li>للتواصل معك بشأن حسابك وخدماتك وطلبات الدعم</li>
        <li>للامتثال للالتزامات القانونية (مثل متطلبات تسجيل النطاقات من ICANN)</li>
        <li>لاكتشاف ومنع ومعالجة المشكلات التقنية والتهديدات الأمنية</li>
        <li>لإرسال اتصالات تسويقية (بموافقتك)</li>
        <li>لتخصيص تجربتك وتقديم توصيات ذات صلة</li>
      </ul>

      <h2>4. مشاركة البيانات والإفصاح</h2>
      <p>نحن لا نبيع معلوماتك الشخصية. قد نشارك بياناتك مع:</p>
      <ul>
        <li><strong>مزودو الخدمات:</strong> معالجو الدفع ومسجلو النطاقات ومزودو البنية التحتية للاستضافة (AWS، Google Cloud، OVH، Cloudflare)</li>
        <li><strong>المتطلبات القانونية:</strong> عندما يقتضي القانون أو أمر المحكمة أو السلطة الحكومية</li>
        <li><strong>ICANN:</strong> بيانات تسجيل النطاق كما هو مطلوب بموجب سياسات ICANN</li>
        <li><strong>شركاء الذكاء الاصطناعي:</strong> قد تتم معالجة بيانات مجهولة الهوية بواسطة مزودي خدمات الذكاء الاصطناعي (OpenAI، Anthropic، Google)</li>
        <li><strong>نقل الأعمال:</strong> فيما يتعلق بالاندماج أو الاستحواذ أو بيع الأصول</li>
      </ul>

      <h2>5. أمن البيانات</h2>
      <p>نطبق إجراءات أمنية متوافقة مع معايير الصناعة تشمل:</p>
      <ul>
        <li>تشفير SSL/TLS لجميع عمليات نقل البيانات</li>
        <li>تخزين مشفر للبيانات الحساسة وكلمات المرور</li>
        <li>عمليات تدقيق أمني منتظمة وتقييمات الثغرات الأمنية</li>
        <li>حماية جدار الحماية والتخفيف من هجمات DDoS (Cloudflare)</li>
        <li>ضوابط الوصول والمصادقة متعددة العوامل للأنظمة الإدارية</li>
        <li>نسخ احتياطي منتظم للبيانات مع تخزين مشفر</li>
      </ul>

      <h2>6. الاحتفاظ بالبيانات</h2>
      <p>نحتفظ ببياناتك الشخصية طالما كان حسابك نشطاً أو حسب الحاجة لتقديم الخدمات. بعد إنهاء الحساب:</p>
      <ul>
        <li>يتم الاحتفاظ ببيانات الحساب لمدة 90 يوماً قبل الحذف الدائم</li>
        <li>يتم الاحتفاظ بسجلات الفوترة لمدة 7 سنوات وفقاً لقانون التجارة الكويتي</li>
        <li>يتم الاحتفاظ بسجلات الخادم لمدة 12 شهراً</li>
        <li>يتم مسح بيانات النسخ الاحتياطي في غضون 30 يوماً من حذف الحساب</li>
      </ul>

      <h2>7. ملفات تعريف الارتباط (الكوكيز)</h2>
      <p>نستخدم ملفات تعريف الارتباط لـ:</p>
      <ul>
        <li><strong>كوكيز أساسية:</strong> المصادقة والأمان وإدارة الجلسات</li>
        <li><strong>كوكيز التحليلات:</strong> فهم أنماط الاستخدام لتحسين خدماتنا</li>
        <li><strong>كوكيز التفضيلات:</strong> تذكر لغتك وتفضيلات العرض</li>
      </ul>
      <p>يمكنك التحكم في ملفات تعريف الارتباط من خلال إعدادات متصفحك.</p>

      <h2>8. حقوقك</h2>
      <p>لديك الحق في:</p>
      <ul>
        <li>الوصول إلى بياناتك الشخصية</li>
        <li>تصحيح المعلومات غير الدقيقة</li>
        <li>طلب حذف بياناتك (راجع صفحة <a href="/delete-account">حذف الحساب</a>)</li>
        <li>تصدير بياناتك بتنسيق قابل للنقل</li>
        <li>سحب الموافقة على الاتصالات التسويقية</li>
        <li>الاعتراض على معالجة البيانات</li>
      </ul>

      <h2>9. خصوصية الأطفال</h2>
      <p>خدماتنا غير موجهة للأفراد الذين تقل أعمارهم عن 18 عاماً. نحن لا نجمع معلومات شخصية من الأطفال عن قصد.</p>

      <h2>10. نقل البيانات الدولي</h2>
      <p>قد تتم معالجة بياناتك في خوادم موجودة خارج الكويت، بما في ذلك الولايات المتحدة وأوروبا ومناطق أخرى حيث يعمل مزودو البنية التحتية لدينا. نضمن وجود ضمانات مناسبة لمثل هذه التحويلات.</p>

      <h2>11. التغييرات على هذه السياسة</h2>
      <p>قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سنخطرك بالتغييرات المهمة عبر البريد الإلكتروني أو من خلال موقعنا.</p>

      <h2>12. اتصل بنا</h2>
      <p>للاستفسارات المتعلقة بالخصوصية:</p>
      <ul>
        <li>البريد الإلكتروني: <a href="mailto:info@roolink.net">info@roolink.net</a></li>
        <li>الهاتف: <a href="tel:+96550580600" dir="ltr">+965 50580600</a></li>
        <li>العنوان: الكويت</li>
      </ul>
    </>
  );

  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="legal-header-bg">
          <div className="legal-shape ls1"></div>
          <div className="legal-shape ls2"></div>
        </div>
        <div className="container">
          <a href="/" className="legal-back"><FiArrowLeft /> {isAr ? 'الرئيسية' : 'Home'}</a>
          <h1>{isAr ? 'سياسة الخصوصية' : 'Privacy Policy'}</h1>
          <p>Roolink</p>
        </div>
      </div>
      <motion.div
        className="container legal-content"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {isAr ? ar : en}
      </motion.div>
    </div>
  );
};

export default PrivacyPolicy;
